import { Injectable } from '@angular/core';
import Graphic from '@arcgis/core/Graphic';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Sketch from '@arcgis/core/widgets/Sketch';
import { Subject } from 'rxjs';

/**
 * Wraps the Esri Sketch widget to let a user draw a polygon/rectangle on the
 * map and select bid items whose markers fall inside. The intersected
 * bid_item_ids are emitted through `selectionChange` so the hosting component
 * can feed them into `MapService.selectedBidItemIds` (which drives the
 * existing multi-select Set used by the side nav).
 *
 * The widget is rendered on a dedicated GraphicsLayer so the drawn polygon
 * doesn't leak into any persistent map state, and both the widget and the
 * layer are torn down on `deactivate`.
 */
@Injectable({ providedIn: 'root' })
export class MapSpatialSelectService {
  private sketch: Sketch | null = null;
  private sketchLayer: GraphicsLayer | null = null;
  // Monotonically incremented on each activate(). Captured in a closure per
  // activation so an in-flight queryFeatures() can tell whether its result
  // belongs to the current session before emitting — prevents a resolved
  // query from clobbering the [] cleared by a mid-flight deactivate.
  private activationId = 0;
  // Tracks whether the current (or most recent) activation actually emitted a
  // selection via a completed draw. Used by deactivate() to decide whether the
  // teardown should emit the cleared-selection []. Without this guard,
  // toggling the spatial-select tool on and off WITHOUT drawing would clobber
  // any pre-existing side-nav selection because `hadState` flipped true the
  // moment the Sketch widget was created.
  private hasEmittedSelection = false;
  // Keyboard handler is attached in activate() and removed in deactivate().
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  public selectionChange = new Subject<string[]>();
  /**
   * Emits the drawn polygon geometry when a shape completes or is edited,
   * and null when draw mode is cancelled/deactivated. The map component
   * listens to feed the summary panel + spatial-summary API call.
   */
  public polygonChange = new Subject<__esri.Polygon | null>();

  isActive(): boolean {
    return this.sketch !== null;
  }

  activate(
    mapView: __esri.MapView,
    sourceLayer: __esri.FeatureLayer | null,
    geometryLayer: __esri.GraphicsLayer | null = null,
  ): void {
    // sourceLayer may be null on projects where the unified-markers layer
    // hasn't been built yet (no bid-item point markers for the current date
    // range). We still let the user draw — the spatial query just skips.

    // Idempotent: mirror MapSwipeService.activate. If this service is already
    // active, tear the prior widget/layer down before creating new ones so
    // repeated toggles don't leak Sketch instances.
    this.deactivate(mapView);

    const myActivation = ++this.activationId;
    this.hasEmittedSelection = false;

    this.sketchLayer = new GraphicsLayer({ id: 'spatial-select-sketch' });
    mapView.map.add(this.sketchLayer);

    this.sketch = new Sketch({
      view: mapView,
      layer: this.sketchLayer,
      creationMode: 'single',
      availableCreateTools: ['polygon'],
      // We auto-start polygon drawing on activate(), so the widget's own
      // toolbar is redundant and clutters the top-right nav area. Hide it
      // entirely — the Sketch still works programmatically.
      visible: false,
    });

    // Brand the drawn polygon + vertices to match the rest of the app.
    // Structure mirrors the permits-web draw mode (30% fill, 4px stroke,
    // 12px vertex dots with 4px outline) but uses SiteNotes blue #2563eb.
    const vm = this.sketch.viewModel;
    vm.polygonSymbol = {
      type: 'simple-fill',
      color: [37, 99, 235, 0.25],
      outline: { color: [37, 99, 235, 1], width: 3 },
    } as __esri.SimpleFillSymbolProperties as any;
    vm.pointSymbol = {
      type: 'simple-marker',
      style: 'circle',
      color: [233, 236, 244, 1],
      size: 10,
      outline: { color: [37, 99, 235, 1], width: 3 },
    } as __esri.SimpleMarkerSymbolProperties as any;
    vm.polylineSymbol = {
      type: 'simple-line',
      color: [37, 99, 235, 1],
      width: 3,
      style: 'solid',
    } as __esri.SimpleLineSymbolProperties as any;
    // Kill the default orange snapping indicator.
    if (vm.snappingOptions) {
      vm.snappingOptions.enabled = false;
    }

    this.sketch.on('create', event => {
      if (event.state !== 'complete') {
        return;
      }
      const polygon = event.graphic.geometry as __esri.Polygon;
      this.polygonChange.next(polygon);
      this.runSpatialQuery(event.graphic, sourceLayer, geometryLayer, myActivation);
      // Enter update mode so the user can drag vertices to adjust the shape.
      // Re-query on every geometry change.
      if (this.sketch && myActivation === this.activationId) {
        this.sketch.update([event.graphic], { tool: 'reshape' });
      }
    });

    // Update mode — fires continuously during a drag/reshape session. The
    // `state === 'complete'` transition only happens when the update session
    // itself ends (e.g. user switches tools), NOT when a single vertex is
    // released, so we also listen for discrete "stop" events on
    // `toolEventInfo.type` (vertex-move-stop, reshape-stop, move-stop,
    // vertex-add, vertex-remove) — any of those means the polygon geometry
    // has settled at a new shape and the summary should refresh.
    this.sketch.on('update', event => {
      const g = event.graphics && event.graphics[0];
      if (!g) return;
      const polygon = g.geometry as __esri.Polygon;
      const info = (event as any).toolEventInfo;
      const dragStopTypes = [
        'vertex-move-stop',
        'reshape-stop',
        'move-stop',
        'vertex-add',
        'vertex-remove',
      ];
      const isDragStop = info && typeof info.type === 'string'
        && dragStopTypes.includes(info.type);
      if (event.state === 'complete' || isDragStop) {
        this.polygonChange.next(polygon);
        this.runSpatialQuery(g, sourceLayer, geometryLayer, myActivation);
      }
    });

    mapView.ui.add(this.sketch, 'top-right');

    // Esc key → undo last vertex during draw. If there are no vertices to
    // undo, Sketch will no-op. During update mode, Esc exits cleanly.
    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !this.sketch) return;
      const state = this.sketch.viewModel.state;
      if (state === 'active') {
        // viewModel.undo() pops the most recent vertex; if none are left
        // it cancels the active create, which we want too.
        this.sketch.viewModel.undo();
        e.stopPropagation();
      } else if (state === 'ready') {
        // Not drawing — treat Esc as "exit the whole tool".
        this.selectionChange.next([]);
        this.polygonChange.next(null);
      }
    };
    document.addEventListener('keydown', this.keydownHandler);

    // Auto-start polygon drawing so the toolbar icon is one-click: click →
    // place vertices. Wait for the widget to be attached/ready before
    // calling create(), otherwise the tool silently fails to activate.
    this.sketch.when(() => {
      // Guard against racing deactivate() between activate() and when()-resolve.
      if (this.sketch && myActivation === this.activationId) {
        this.sketch.create('polygon');
      }
    });
  }

  deactivate(mapView: __esri.MapView): void {
    // Only emit the cleared-selection signal if the user actually drew a
    // polygon during this (or a prior) activation. Merely toggling the tool
    // on-and-off without drawing should leave any pre-existing bid-item
    // selection (e.g. made via side nav) untouched.
    const shouldClear = this.hasEmittedSelection;

    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }

    if (this.sketch) {
      mapView.ui.remove(this.sketch);
      this.sketch.destroy();
      this.sketch = null;
    }
    if (this.sketchLayer) {
      mapView.map.remove(this.sketchLayer);
      this.sketchLayer.destroy();
      this.sketchLayer = null;
    }

    // Bump the activation counter so any query in flight from the prior
    // session sees a mismatch and drops its result.
    this.activationId++;

    if (shouldClear) {
      this.selectionChange.next([]);
    }
    this.polygonChange.next(null);
    this.hasEmittedSelection = false;
  }

  private async runSpatialQuery(
    drawnGraphic: Graphic,
    sourceLayer: __esri.FeatureLayer | null,
    geometryLayer: __esri.GraphicsLayer | null,
    activationId: number,
  ): Promise<void> {
    const polygon = drawnGraphic.geometry;
    if (!polygon) {
      return;
    }

    // Match the drawn shape against the as-built geometry itself. The marker
    // layer alone isn't enough: a marker sits at the averaged centroid of a
    // ~3.7m location group, so a polygon drawn around an item's own line can
    // enclose a neighbour's marker and miss that item entirely.
    const geometryIds = geometryLayer
      ? this.collectGeometryLayerBidItemIds(polygon as __esri.Polygon, geometryLayer)
      : [];

    if (!sourceLayer) {
      if (activationId !== this.activationId) {
        return;
      }
      this.hasEmittedSelection = true;
      this.selectionChange.next(geometryIds);
      return;
    }

    const query = sourceLayer.createQuery();
    query.geometry = polygon;
    query.spatialRelationship = 'intersects';
    query.returnGeometry = false;
    // Include both the singular bid_item_id (standard markers) and the plural
    // CSV bid_item_ids (multi-item composite markers — see
    // UnifiedMarkerService). markerType gates out photo markers so a photo
    // tagged to an unrelated bid item can't leak into the selection.
    query.outFields = ['bid_item_id', 'bid_item_ids', 'markerType', 'uuid'];

    try {
      const result = await sourceLayer.queryFeatures(query);
      // Drop stale results if the service was torn down or re-activated
      // mid-flight. Without this, a slow query from session N-1 could
      // undo the [] emitted by deactivate() for session N.
      if (activationId !== this.activationId) {
        return;
      }
      const ids = new Set(this.extractBidItemIds(result.features));
      geometryIds.forEach(id => ids.add(id));
      this.hasEmittedSelection = true;
      this.selectionChange.next(Array.from(ids));
    } catch (err) {
      // Surface query failures to the console (picked up by Sentry's
      // console-error capture in production). Swallowing would leave the
      // user with a silently empty selection.
      console.error('[MapSpatialSelectService] queryFeatures failed', err);
    }
  }

  /**
   * Bid items whose as-built geometry intersects the drawn polygon. Site
   * boundary graphics are skipped — they cover the whole site, so any polygon
   * would "hit" them.
   */
  private collectGeometryLayerBidItemIds(
    polygon: __esri.Polygon,
    geometryLayer: __esri.GraphicsLayer,
  ): string[] {
    const graphics = geometryLayer.graphics ? geometryLayer.graphics.toArray() : [];
    if (!graphics.length) {
      return [];
    }
    // Graphics are stored in WGS84 while the sketch returns the view's Web
    // Mercator, and geometryEngine needs both operands in one reference.
    const geographicPolygon = polygon.spatialReference?.wkid === 4326
      ? polygon
      : webMercatorUtils.webMercatorToGeographic(polygon) as __esri.Polygon;

    const ids = new Set<string>();
    for (const graphic of graphics) {
      const attrs = (graphic.attributes || {}) as Record<string, unknown>;
      if (attrs['isSiteGeometry']) {
        continue;
      }
      const bidItemId = attrs['bidItemId'] || attrs['bid_item_id'];
      if (typeof bidItemId !== 'string' || !bidItemId || ids.has(bidItemId)) {
        continue;
      }
      const geometry = graphic.geometry;
      if (!geometry) {
        continue;
      }
      const probe = geometry.spatialReference?.wkid === 4326 ? geographicPolygon : polygon;
      if (geometryEngine.intersects(probe, geometry)) {
        ids.add(bidItemId);
      }
    }
    return Array.from(ids);
  }

  private extractBidItemIds(graphics: { attributes: Record<string, unknown> }[]): string[] {
    const set = new Set<string>();
    for (const g of graphics) {
      const attrs = g.attributes || {};
      const markerType = attrs['markerType'];

      // Only bid-item markers and multi-item-location markers participate in
      // spatial select. Photo markers with bid_item_id would otherwise leak
      // into the selection (e.g., a photo tied to an unrelated bid item that
      // happens to be inside the drawn polygon).
      if (markerType !== 'bid_item' && markerType !== 'multi_item_location') {
        continue;
      }

      const singleId = attrs['bid_item_id'];
      if (typeof singleId === 'string' && singleId.length > 0) {
        set.add(singleId);
      }

      const csvIds = attrs['bid_item_ids'];
      if (typeof csvIds === 'string' && csvIds.length > 0) {
        for (const id of csvIds.split(',')) {
          const trimmed = id.trim();
          if (trimmed.length > 0) {
            set.add(trimmed);
          }
        }
      }
    }
    return Array.from(set);
  }
}
